/**
 * Overview tab — the default landing view, rebuilt around the two heroes
 * (decisions doc Q1):
 *
 *   [ LiveStrip (today-so-far, 30s polling) ]
 *   [ HERO 1 — Are people using it: DAU/WAU (opened the app), chat, sessions ]
 *   [ HERO 2 — Trial pipeline: in-trial, starts, trial→paid, conversion % ]
 *   [ Secondary KPI grid (remaining server tiles, heroes filtered out) ]
 *   [ Active users trend | Throughput trend (split — different scales) ]
 *   [ 2-col row: Relationship tier donut | Latest unacked alerts ]
 */
import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAnalyticsQuery } from '../hooks/useAnalyticsQuery';
import { adminMutate } from '../../../../utils/adminApi';
import { type AnalyticsRange } from '../hooks/useAnalyticsRange';
import type { OverviewPayload } from '../types';
import { KpiTile } from '../components/KpiTile';
import { ChartCard } from '../components/ChartCard';
import { TrendChart } from '../components/TrendChart';
import { LiveStrip } from '../components/LiveStrip';
import { AnomalyAlertCard } from '../components/AnomalyAlertCard';
import { RelationshipTierDonut } from '../components/RelationshipTierDonut';
import { formatInt } from '../format';
import { EmptyState } from '../components/EmptyState';
import { formatAnalyticsDayShort, todayInAnalyticsTz } from '../timezone';

export interface OverviewTabProps {
  range: AnalyticsRange;
}

export const OverviewTab: React.FC<OverviewTabProps> = ({ range }) => {
  const queryClient = useQueryClient();
  const { data, isLoading, error, refetch } = useAnalyticsQuery<OverviewPayload>(
    ['overview', range.queryString],
    `/api/admin/analytics/overview?${range.queryString}`,
  );

  const ackMutation = useMutation({
    mutationFn: async (id: number) =>
      adminMutate<unknown>(`/api/admin/analytics/alerts/events/${id}/ack`, 'POST'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['analytics', 'overview'] });
    },
  });

  const payload = data?.data;
  const trend = payload?.trend_12w ?? [];
  const heroEng = payload?.hero_engagement;
  const pipeline = payload?.trial_pipeline;
  // Tiles promoted into the hero rows — filtered from the secondary grid so they
  // don't render twice. Label-matched (the kpis array is server-driven); worst
  // case of a label rename is a cosmetic duplicate tile, never lost data.
  const HERO_LABELS = new Set(['Sessions completed', 'Chat messages', 'Trial starts', 'Trial → Paid']);
  const kpis = (payload?.kpis ?? []).filter((k) => !HERO_LABELS.has(k.label));
  const todayIso = todayInAnalyticsTz();

  return (
    <div className="space-y-6">
      <LiveStrip />

      {/* HERO 1 — are people using it */}
      <section>
        <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          Are people using it — opened the app · chat · exercise
        </h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <KpiTile
            label="DAU (opened the app)"
            value={formatInt(heroEng?.dau)}
            hint={heroEng ? `${formatInt(heroEng.wau)} WAU · latest complete day` : undefined}
            loading={isLoading}
          />
          <KpiTile
            label="Stickiness (DAU / MAU)"
            value={heroEng?.stickiness_pct != null ? `${heroEng.stickiness_pct.toFixed(1)}%` : '—'}
            loading={isLoading}
          />
          <KpiTile
            label="Chat messages"
            value={formatInt(heroEng?.chat_messages)}
            deltaPct={heroEng?.chat_messages_delta_pct}
            loading={isLoading}
          />
          <KpiTile
            label="Sessions completed"
            value={formatInt(heroEng?.sessions_completed)}
            deltaPct={heroEng?.sessions_delta_pct}
            loading={isLoading}
          />
        </div>
      </section>

      {/* HERO 2 — trial acquisition & conversion (revenue scope, matches RevenueCat) */}
      <section>
        <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          Trial pipeline — acquisition &amp; conversion (matches RevenueCat)
        </h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <KpiTile
            label="Currently in trial"
            value={formatInt(pipeline?.currently_in_trial)}
            hint="live · ignores date range"
            loading={isLoading}
          />
          <KpiTile
            label="Trial starts"
            value={formatInt(pipeline?.trial_starts)}
            deltaPct={pipeline?.trial_starts_delta_pct}
            loading={isLoading}
          />
          <KpiTile
            label="Trial → Paid"
            value={formatInt(pipeline?.trial_to_paid)}
            deltaPct={pipeline?.trial_to_paid_delta_pct}
            loading={isLoading}
          />
          <KpiTile
            label="Conversion rate"
            value={pipeline?.conversion_pct != null ? `${pipeline.conversion_pct.toFixed(1)}%` : 'N/A'}
            hint="trial→paid ÷ trial starts, selected range"
            loading={isLoading}
          />
        </div>
      </section>

      {/* Secondary KPI grid (server-driven tiles minus the promoted heroes) */}
      <section>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {isLoading &&
            Array.from({ length: 4 }).map((_, i) => <KpiTile key={i} label="" value="" loading />)}
          {!isLoading &&
            kpis.map((k) => (
              <KpiTile
                key={k.label}
                label={k.label}
                value={formatInt(k.value)}
                deltaPct={k.delta_pct}
              />
            ))}
        </div>
      </section>

      {/* 12-week trends — split into two charts: active-users vs throughput.
          One shared axis made signups (~0–10/day) invisible next to MAU. */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ChartCard
          title="Active users (opened the app) — 12 weeks"
          subtitle="DAU / WAU / MAU. Steps up where the opened-app signal went live (not backfillable). Today refreshes every 10 minutes."
          loading={isLoading}
          error={error?.message ?? null}
          isEmpty={!trend.length}
          onRetry={() => refetch()}
        >
          <TrendChart
            data={trend}
            xKey="day"
            type="line"
            height={260}
            series={[
              { key: 'dau', label: 'DAU' },
              { key: 'wau', label: 'WAU' },
              { key: 'mau', label: 'MAU' },
            ]}
            xFormat={(iso) => formatDayShort(iso, todayIso)}
          />
        </ChartCard>
        <ChartCard
          title="Throughput — 12 weeks"
          subtitle="Sessions completed, workouts, and signups per day. Today refreshes every 10 minutes."
          loading={isLoading}
          error={error?.message ?? null}
          isEmpty={!trend.length}
          onRetry={() => refetch()}
        >
          <TrendChart
            data={trend}
            xKey="day"
            type="area"
            height={260}
            series={[
              { key: 'cards_completed', label: 'Sessions' },
              { key: 'workouts_completed', label: 'Workouts' },
              { key: 'signups', label: 'Signups' },
            ]}
            xFormat={(iso) => formatDayShort(iso, todayIso)}
          />
        </ChartCard>
      </div>

      {/* 2-col: tier donut + alerts */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <ChartCard
          title="Relationship tiers"
          subtitle="Coaching-relationship depth (not subscription tier) — latest snapshot"
          loading={isLoading}
          error={error?.message ?? null}
          isEmpty={!payload?.tier_breakdown?.length}
          onRetry={() => refetch()}
          className="lg:col-span-1"
        >
          <RelationshipTierDonut data={payload?.tier_breakdown ?? []} />
        </ChartCard>

        <ChartCard
          title="Latest anomalies"
          subtitle="Unacknowledged alert events"
          loading={isLoading}
          error={error?.message ?? null}
          onRetry={() => refetch()}
          className="lg:col-span-2"
          isEmpty={false}
        >
          {payload && payload.alerts.length > 0 ? (
            <div className="space-y-2">
              {payload.alerts.map((ev) => (
                <AnomalyAlertCard
                  key={ev.id}
                  event={ev}
                  onAcknowledge={(id) => ackMutation.mutate(id)}
                  isAcknowledging={ackMutation.isPending}
                />
              ))}
            </div>
          ) : (
            <EmptyState
              variant="empty"
              title="All quiet"
              message="No threshold breaches to report right now."
            />
          )}
        </ChartCard>
      </div>
    </div>
  );
};

// Analytics-TZ-aware date formatter — see web/src/pages/admin/analytics/timezone.ts
const formatDayShort = formatAnalyticsDayShort;
